/**
 * MIKUNA PDP — Buy box logic — v19
 * v12: Gallery thumbnail click handler added (Option A: swap, one image visible)
 * v13: Flavor swap now also updates back-of-bag (thumb 1 + main 1) by reading
 *      productMedia and finding the image immediately after the variant featured image.
 *      Requires productMedia array in [data-mk-product-data] JSON blob.
 * v14: Size-aware gallery + pricing so the 8 Travel Sachets selection is coherent.
 *      - updateGalleryImage() now shows the selected variant's OWN image when a
 *        non-bag size (e.g. "8 Travel Sachets") is chosen in one-time tier 1, and
 *        falls back to the bag front/back everywhere else. It is now also called on
 *        size, mode, and tier changes — not just on flavor change.
 *      - The one-time tier-1 card now prices from the actually-selected variant
 *        (bag OR sachets) and shows that size's serving count, instead of always
 *        using the 15-serving bag. The headline price and ATC already did this; the
 *        tier card now matches, so everything moves together.
 *      NOTE: the sachet photo only appears if the "8 Travel Sachets" variant has an
 *      image ASSIGNED to it in Shopify (Products > Chocho > that variant > image).
 *      Without one, the gallery gracefully keeps showing the bag image.
 * v15: (REVERTED in v16) Hid the thumbnail rail in the sachet view. Hiding the
 *      rail conflicted with the gallery layout and broke it.
 * v16: Removed the v15 rail-hide. Gallery is back to the working v14 behavior
 *      (sachet shows on the main image; the rail is left intact).
 * v17: Sachet gallery now shows the correct front AND back per flavor. Sachet images
 *      are mapped explicitly by filename (resolved against productMedia) because they
 *      don't follow the bag's front-then-back media ordering: page 1 = sachet front,
 *      page 2 = sachet back. Fixes Vanilla's wrong front and the bag-back-on-page-2
 *      issue for all three flavors. No rail hiding (layout untouched); pages 3-4 keep
 *      the shared info graphics. Pure/Cacao fronts still come from their variant image
 *      (already correct); Vanilla's front is mapped (its variant image wasn't the
 *      sachet shot). To add/adjust a sachet image, edit SACHET_IMG_IDS below.
 * v18: Pricing removed from the tier cards. The Add-to-cart button (and the
 *      headline price) are now the single source of truth for "how much"; the
 *      cards show benefits only. Per-card price + per-serving are dropped at init;
 *      savings lines are benefit-only (percent, not dollars), one-time tier 1 has
 *      no perk line by design. Tier metas are now set in JS for every tier so the
 *      "·" separator always renders clean (the live template's separator was
 *      mojibaked). Subscribe tiers no longer claim a free shaker, since the
 *      auto-add rule excludes subscriptions. Price math (headline + ATC) is
 *      untouched — only the card *display* changed.
 * v19: Fixed the 15-serving (bag) gallery showing the 8ct sachet photo for a
 *      flavor whose bag variant image is unreliable (Cinnamon Horchata). Added
 *      BAG_IMG_IDS — an explicit bag-front override resolved against productMedia,
 *      mirroring SACHET_IMG_IDS — and consulted it in updateGalleryImage's bag
 *      fallback (back derived from the next media item). Only affects flavors
 *      listed in BAG_IMG_IDS; all others keep the existing FLAVOR_IMAGE_PAIRS path.
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-mk-buybox]');
  if (!root) return;

  const dataNode = document.querySelector('[data-mk-product-data]');
  if (!dataNode) return;

  let DATA;
  try { DATA = JSON.parse(dataNode.textContent); }
  catch (e) { console.error('[Mikuna PDP] Failed to parse product data', e); return; }

  const FLAVORS = [...new Set(DATA.variants.map(v => v.flavor))];
  const SIZES = [...new Set(DATA.variants.map(v => v.size))];
  const SIZE_15 = SIZES.find(s => s && s.indexOf('15') !== -1);
  const SIZE_8 = SIZES.find(s => s && s.indexOf('8') !== -1);
  const BASE_PRICE_CENTS = DATA.variants[0] ? DATA.variants[0].price : DATA.basePrice;

  const SUBSCRIBE_MAX_TIER = 3;
  const ONETIME_TIER4_MIN = 4;
  const ONETIME_TIER4_MAX = 5;

  const FLAVOR_IMAGES = {};
  DATA.variants.forEach(v => {
    if (v.flavor && v.image && !FLAVOR_IMAGES[v.flavor]) {
      FLAVOR_IMAGES[v.flavor] = v.image;
    }
  });

  // Build flavor -> {front, back} image pair from productMedia order.
  // Assumes Shopify media is ordered front-then-back for each variant.
  const FLAVOR_IMAGE_PAIRS = {};
  if (DATA.productMedia && DATA.productMedia.length) {
    DATA.variants.forEach(v => {
      if (!v.flavor || !v.image || FLAVOR_IMAGE_PAIRS[v.flavor]) return;
      const variantFilename = v.image.split('/').pop().split('?')[0];
      const mediaIndex = DATA.productMedia.findIndex(url => {
        const mediaFilename = url.split('/').pop().split('?')[0];
        return mediaFilename === variantFilename;
      });
      if (mediaIndex === -1) return;
      FLAVOR_IMAGE_PAIRS[v.flavor] = {
        front: DATA.productMedia[mediaIndex],
        back: DATA.productMedia[mediaIndex + 1] || null
      };
    });
  }

  // ---- Sachet gallery images (8 Travel Sachets) ------------------------------
  // Sachets don't follow the bag's front-then-back media ordering, so their front
  // and back are mapped explicitly by image filename and resolved against
  // productMedia at runtime. Front falls back to the sachet variant's own image
  // when not mapped (Pure/Cacao already resolve correctly that way). Vanilla's front
  // is mapped because its variant image wasn't the sachet shot. Keys are lowercased
  // flavor names; values are productMedia filename fragments.
  const SACHET_IMG_IDS = {
    'pure chocho': { back: 'chocho-plant-protein-by-mikuna-3070562' },
    'cacao':       { back: 'chocho-plant-protein-by-mikuna-5450958' },
    'vanilla':     { front: 'chocho-plant-protein-by-mikuna-9297872',
                     back:  'chocho-plant-protein-by-mikuna-9343970' }
  };

  const findMediaUrl = (idFragment) => {
    if (!idFragment || !DATA.productMedia) return null;
    return DATA.productMedia.find(u => u.indexOf(idFragment) !== -1) || null;
  };

  const sachetImagesFor = (flavor) => SACHET_IMG_IDS[(flavor || '').trim().toLowerCase()] || {};

  // ---- Bag gallery front overrides (15-serving) ------------------------------
  // The 15-serving "front" normally comes from FLAVOR_IMAGE_PAIRS, which is built
  // from the first variant image found for a flavor. When a flavor's bag variant
  // image is unset or points at the sachet shot, that fallback shows the WRONG
  // photo (the sachet) for the bag size. Map the correct bag front by filename
  // here; it's resolved against productMedia at runtime and the back is derived
  // from the next media item (front-then-back), same as every other flavor.
  // Keys are matched as a substring of the flavor name (lowercased), so 'horchata'
  // matches "Cinnamon Horchata". To add/adjust a bag image, edit BAG_IMG_IDS.
  const BAG_IMG_IDS = {
    'horchata': { front: 'chocho-plant-protein-by-mikuna-1443146' }
  };
  const bagImagesFor = (flavor) => {
    const lower = (flavor || '').trim().toLowerCase();
    if (!lower) return {};
    const hit = Object.keys(BAG_IMG_IDS).find(k => lower.indexOf(k) !== -1);
    return hit ? BAG_IMG_IDS[hit] : {};
  };

  const isComingSoonFlavor = (flavor) => {
    const lower = (flavor || '').toLowerCase();
    return (lower.indexOf('cinnamon') !== -1 || lower.indexOf('horchata') !== -1)
      && DATA.config && DATA.config.cinnamonComingSoon;
  };

  const AVAILABLE_FLAVORS = FLAVORS.filter(f => !isComingSoonFlavor(f));

  const bundleKey = (flavor, size) => `${flavor}__${size}`;

  const initBundle = () => {
    const b = {};
    DATA.variants.forEach(v => {
      if (!isComingSoonFlavor(v.flavor)) {
        b[bundleKey(v.flavor, v.size)] = 0;
      }
    });
    return b;
  };

  const state = {
    mode: DATA.hasSubscription ? 'subscribe' : 'onetime',
    subscribeTier: 1,
    onetimeTier: 1,
    tier4Bags: 4,
    flavor: DATA.variants[0] ? DATA.variants[0].flavor : FLAVORS[0],
    size: DATA.variants[0] ? DATA.variants[0].size : SIZES[0],
    cadencePlanId: DATA.sellingPlans[0] ? DATA.sellingPlans[0].id : null,
    bundle: initBundle()
  };

  if (state.mode === 'subscribe' && SIZE_15) state.size = SIZE_15;

  const els = {
    form: document.querySelector('[data-mk-form]'),
    variantInput: root.querySelector('[data-mk-variant-id]'),
    qtyInput: root.querySelector('[data-mk-qty-input]'),
    sellingPlanInput: root.querySelector('[data-mk-selling-plan]'),
    bundleProp: root.querySelector('[data-mk-bundle-prop]'),
    priceMain: root.querySelector('[data-mk-price-main]'),
    priceStrike: root.querySelector('[data-mk-price-strike]'),
    purchaseBody: root.querySelector('[data-mk-purchase-body]'),
    cadenceLabel: root.querySelector('[data-mk-cadence-label]'),
    cadenceRow: root.querySelector('[data-mk-cadence-row]'),
    flavorLabel: root.querySelector('[data-mk-flavor-label]'),
    sizeLabel: root.querySelector('[data-mk-size-label]'),
    onetimeTiersWrap: root.querySelector('[data-mk-onetime-tiers]'),
    flavorBuilder: root.querySelector('[data-mk-flavor-builder]'),
    flavorBuilderRows: root.querySelector('[data-mk-flavor-builder-rows]'),
    flavorBuilderSummary: root.querySelector('[data-mk-flavor-builder-summary]'),
    flavorBuilderCount: root.querySelector('[data-mk-flavor-builder-count]'),
    tier4Display: root.querySelector('[data-mk-tier4-display]'),
    tier4Bags: root.querySelector('[data-mk-tier4-bags]'),
    tier4Servings: root.querySelector('[data-mk-tier4-servings]'),
    tier4Supply: root.querySelector('[data-mk-tier4-supply]'),
    ppsLine: root.querySelector('[data-mk-pps-line]'),
    crossSell: root.querySelector('[data-mk-switch-to-subscribe]'),
    atc: root.querySelector('[data-mk-atc]'),
    atcPrice: root.querySelector('[data-mk-atc-price]')
  };

  // v18: Pricing is removed from the tier cards. The single source of truth for
  // "how much" is the headline price + the Add-to-cart button; the cards now
  // communicate benefits only. Drop the per-card price/per-serving block once,
  // up front, so nothing has to keep clearing it on every render.
  root.querySelectorAll('.mk-tier-card-pricing').forEach((el) => el.remove());

  const fmt = (cents) => '$' + (cents / 100).toFixed(2);

  const findVariant = (flavor, size) => {
    return DATA.variants.find(v => v.flavor === flavor && v.size === size && v.available)
      || DATA.variants.find(v => v.flavor === flavor && v.size === size);
  };

  const find15ServingVariant = (flavor) => {
    return DATA.variants.find(v => v.flavor === flavor && v.size === SIZE_15 && v.available)
      || DATA.variants.find(v => v.flavor === flavor && v.size === SIZE_15);
  };

  const swapImage = (imgEl, newUrl) => {
    if (!imgEl || !newUrl) return;
    imgEl.src = newUrl;
    if (imgEl.srcset) imgEl.srcset = '';
  };

  const updateGalleryImage = (flavor) => {
    // The single-size selector (bag vs sachets) is only in play in one-time tier 1.
    // There, a non-bag size should show that variant's OWN photo. Everywhere else
    // (subscribe, one-time tier 2+, or the bag size) shows the flavor's bag front/back.
    const showingSingleSize = state.mode === 'onetime' && state.onetimeTier === 1;
    const isSachetSize = state.size && state.size.indexOf('15') === -1;

    let frontUrl = null;
    let backUrl = null;

    if (showingSingleSize && isSachetSize) {
      const overrides = sachetImagesFor(flavor);
      const variant = findVariant(flavor, state.size);
      // Front: mapped sachet front if present, else the sachet variant's own image.
      frontUrl = findMediaUrl(overrides.front) || (variant && variant.image ? variant.image : null);
      // Back: mapped sachet back (sachets have no reliable media adjacency to infer from).
      backUrl = findMediaUrl(overrides.back) || null;
    }

    // Default / fallback (bag view, also used if a sachet variant has no image).
    if (!frontUrl) {
      // If this flavor has an explicit bag-front override (its bag variant image
      // is unreliable), use it and derive the back from the next media item.
      const bagFront = findMediaUrl(bagImagesFor(flavor).front);
      if (bagFront) {
        frontUrl = bagFront;
        const idx = DATA.productMedia ? DATA.productMedia.indexOf(bagFront) : -1;
        backUrl = (idx !== -1 && DATA.productMedia[idx + 1]) ? DATA.productMedia[idx + 1] : null;
      } else {
        const pair = FLAVOR_IMAGE_PAIRS[flavor];
        frontUrl = pair ? pair.front : FLAVOR_IMAGES[flavor];
        backUrl = pair ? pair.back : null;
      }
    }

    if (!frontUrl) return;

    const mainImages = document.querySelectorAll('.mk-gallery-main img');
    const thumbs = document.querySelectorAll('[data-mk-thumb-index]');

    // Swap front (position 0)
    swapImage(mainImages[0], frontUrl);
    const thumb0Img = thumbs[0] ? thumbs[0].querySelector('img') : null;
    swapImage(thumb0Img, frontUrl);

    // Swap back (position 1) if we have one
    if (backUrl) {
      swapImage(mainImages[1], backUrl);
      const thumb1Img = thumbs[1] ? thumbs[1].querySelector('img') : null;
      swapImage(thumb1Img, backUrl);
    }

    // Reset active to thumb 0
    thumbs.forEach(t => t.classList.remove('is-active'));
    if (thumbs[0]) thumbs[0].classList.add('is-active');

    // Show only the first main image, hide the rest
    mainImages.forEach((img, i) => {
      img.style.display = i === 0 ? 'block' : 'none';
    });
  };

  // ============================================================
  // GALLERY THUMBNAILS — Option A: swap (one main image visible)
  // ============================================================
  const initGalleryThumbs = () => {
    const thumbs = document.querySelectorAll('[data-mk-thumb-index]');
    const mainImages = document.querySelectorAll('.mk-gallery-main img');
    if (!thumbs.length || !mainImages.length) return;

    // On init: show only the first image, hide the rest
    mainImages.forEach((img, i) => {
      img.style.display = i === 0 ? 'block' : 'none';
    });

    const showImageAtIndex = (idx) => {
      mainImages.forEach((img, i) => {
        img.style.display = i === idx ? 'block' : 'none';
      });
      thumbs.forEach(t => t.classList.remove('is-active'));
      const activeThumb = document.querySelector('[data-mk-thumb-index="' + idx + '"]');
      if (activeThumb) activeThumb.classList.add('is-active');
    };

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        const idx = parseInt(thumb.getAttribute('data-mk-thumb-index'), 10);
        if (!isNaN(idx)) showImageAtIndex(idx);
      });
    });
  };

  const getFlavorDotClass = (flavor) => {
    const lower = (flavor || '').toLowerCase();
    if (lower.indexOf('vanilla') !== -1) return 'vanilla';
    if (lower.indexOf('cacao') !== -1) return 'cacao';
    if (lower.indexOf('cinnamon') !== -1 || lower.indexOf('horchata') !== -1) return 'cinnamon';
    return 'pure';
  };

  const bundleTotalQty = () => Object.values(state.bundle).reduce((a, b) => a + b, 0);

  const onetimeTierDiscount = (tier) => {
    if (tier === 1 || tier === 2) return 0;
    if (tier === 3) return 0.10;
    if (tier === 4) return 0.15;
    return 0;
  };

  const onetimeTierBagCount = (tier) => tier === 4 ? state.tier4Bags : tier;

  const calculatePPS = (totalCents, totalServings) => {
    if (!totalServings) return 0;
    return Math.round(totalCents / totalServings);
  };

  // Servings per single unit of a given size: 15-serving bag vs 8-count sachets.
  const servingsForSize = (size) => (size && size.indexOf('15') !== -1) ? 15 : 8;

  const calculateBundleBasePrice = () => {
    let total = 0;
    Object.entries(state.bundle).forEach(([key, qty]) => {
      if (qty <= 0) return;
      const [flavor, size] = key.split('__');
      const variant = findVariant(flavor, size);
      if (variant) total += variant.price * qty;
    });
    return total;
  };

  const calculateBundleServings = () => {
    let total = 0;
    Object.entries(state.bundle).forEach(([key, qty]) => {
      if (qty <= 0) return;
      const [, size] = key.split('__');
      const servings = (size && size.indexOf('15') !== -1) ? 15 : 8;
      total += servings * qty;
    });
    return total;
  };

  const calculateSubscribeTierPrice = (tier) => {
    const variant = find15ServingVariant(state.flavor);
    const unitPrice = variant ? variant.price : BASE_PRICE_CENTS;
    const baseTotal = unitPrice * tier;
    const finalTotal = Math.round(baseTotal * (1 - DATA.config.subscribeDiscount));
    return { baseTotal, finalTotal, savings: baseTotal - finalTotal };
  };

  const calculateOnetimeTier1Price = () => {
    const variant = findVariant(state.flavor, state.size);
    const unitPrice = variant ? variant.price : BASE_PRICE_CENTS;
    return { baseTotal: unitPrice, finalTotal: unitPrice, savings: 0 };
  };

  const calculateOnetimeTierStaticPrice = (tier) => {
    const variant = find15ServingVariant(state.flavor);
    const unitPrice = variant ? variant.price : BASE_PRICE_CENTS;
    const bags = onetimeTierBagCount(tier);
    const baseTotal = unitPrice * bags;
    const disc = onetimeTierDiscount(tier);
    const finalTotal = Math.round(baseTotal * (1 - disc));
    return { baseTotal, finalTotal, savings: baseTotal - finalTotal };
  };

  const updateSubscribeTierCards = () => {
    // Benefits only — the price is shown on the headline + Add-to-cart button.
    // Subscription = 20% off every order; free shipping kicks in from 2 items
    // (the $50 threshold). No free shaker: the auto-add rule excludes
    // subscriptions, so we don't promise one here.
    const SUB_META = {
      1: '15 servings',
      2: '30 servings · 1-month supply',
      3: '45 servings · 6-week supply'
    };
    for (let tier = 1; tier <= SUBSCRIBE_MAX_TIER; tier++) {
      const savingsEl = root.querySelector('[data-mk-tier-savings="' + tier + '"]');
      if (savingsEl) {
        savingsEl.textContent = tier >= 2 ? 'Save 20% + Free shipping' : 'Save 20%';
      }
      const metaEl = root.querySelector('[data-mk-tier="' + tier + '"] .mk-tier-card-meta');
      if (metaEl && SUB_META[tier]) metaEl.textContent = SUB_META[tier];
    }
    root.querySelectorAll('[data-mk-tier]').forEach(b => {
      b.classList.toggle('is-active', parseInt(b.dataset.mkTier, 10) === state.subscribeTier);
    });
  };

  const updateOnetimeTierCards = () => {
    // Benefits only — price is shown on the headline + Add-to-cart button.
    // Tier 1 is the baseline (no perk line); each step up adds something:
    // free shipping at 2, then a 10%/15% discount + free shaker at 3/4.
    const BENEFIT = {
      1: '',
      2: 'Free shipping',
      3: 'Save 10% + Free shipping + Free shaker',
      4: 'Save 15% + Free shipping + Free shaker'
    };
    for (let tier = 1; tier <= 4; tier++) {
      const savingsEl = root.querySelector('[data-mk-onetime-savings="' + tier + '"]');
      if (savingsEl) {
        const txt = BENEFIT[tier] || '';
        savingsEl.textContent = txt;
        // Tier 1 has no benefit line — collapse it so there's no orphan divider.
        savingsEl.style.display = txt ? '' : 'none';
      }
    }

    // Tier metas (servings + supply). Set here so the "·" separator is always
    // clean, regardless of how the live template was encoded. Tier 1 tracks the
    // selected size (15 bag / 8 sachets); tier 4 tracks the stepper quantity.
    const setMeta = (tier, text) => {
      const m = root.querySelector('[data-mk-onetime-tier="' + tier + '"] .mk-tier-card-meta');
      if (m) m.textContent = text;
    };
    setMeta(1, servingsForSize(state.size) + ' servings');
    setMeta(2, '30 servings · 1-month supply');
    setMeta(3, '45 servings · 6-week supply');
    setMeta(4, (state.tier4Bags * 15) + ' servings · ' + (state.tier4Bags === 5 ? '2.5-month' : '2-month') + ' supply');

    // Tier-4 quantity: name number + stepper display (servings/supply are handled
    // by setMeta(4) above, so the old sub-span writes are no longer needed).
    if (els.tier4Display) els.tier4Display.textContent = state.tier4Bags;
    if (els.tier4Bags) els.tier4Bags.textContent = state.tier4Bags;

    root.querySelectorAll('[data-mk-onetime-tier]').forEach(b => {
      b.classList.toggle('is-active', parseInt(b.dataset.mkOnetimeTier, 10) === state.onetimeTier);
    });
  };

  const renderBuilderRow = (flavor, size, dotClass, qty, canIncrement, canDecrement, isComingSoon) => {
    const key = bundleKey(flavor, size);
    return `
      <div class="mk-flavor-builder-row${isComingSoon ? ' is-disabled' : ''}">
        <div class="mk-flavor-builder-info">
          <span class="mk-flavor-dot mk-flavor-dot--${dotClass}"></span>
          <div>
            <div class="mk-flavor-builder-name">${flavor}</div>
            ${isComingSoon ? '<div class="mk-flavor-builder-meta">Coming May 28</div>' : ''}
          </div>
        </div>
        <div class="mk-flavor-builder-stepper">
          <button type="button" class="mk-flavor-builder-stepper-btn"
            data-mk-builder-key="${key}" data-mk-builder-action="-"
            ${canDecrement && !isComingSoon ? '' : 'disabled'}
            aria-label="Remove ${flavor}">&minus;</button>
          <span class="mk-flavor-builder-stepper-display">${qty}</span>
          <button type="button" class="mk-flavor-builder-stepper-btn"
            data-mk-builder-key="${key}" data-mk-builder-action="+"
            ${canIncrement && !isComingSoon ? '' : 'disabled'}
            aria-label="Add ${flavor}">+</button>
        </div>
      </div>
    `;
  };

  const renderFlavorBuilderRows = () => {
    if (!els.flavorBuilderRows) return;

    const targetUnits = onetimeTierBagCount(state.onetimeTier);
    const total = bundleTotalQty();
    let html = '';

    const hasBags = AVAILABLE_FLAVORS.some(f => DATA.variants.some(v => v.flavor === f && v.size === SIZE_15));
    if (hasBags) {
      html += '<div class="mk-builder-section-label">15-serving bags</div>';
      AVAILABLE_FLAVORS.forEach(flavor => {
        const variant = DATA.variants.find(v => v.flavor === flavor && v.size === SIZE_15);
        if (!variant) return;
        const qty = state.bundle[bundleKey(flavor, SIZE_15)] || 0;
        html += renderBuilderRow(flavor, SIZE_15, getFlavorDotClass(flavor), qty, total < targetUnits, qty > 0, false);
      });
      FLAVORS.filter(f => isComingSoonFlavor(f)).forEach(flavor => {
        html += renderBuilderRow(flavor, SIZE_15, getFlavorDotClass(flavor), 0, false, false, true);
      });
    }

    const hasSachets = AVAILABLE_FLAVORS.some(f => DATA.variants.some(v => v.flavor === f && v.size === SIZE_8));
    if (hasSachets && SIZE_8) {
      html += '<div class="mk-builder-section-label">8-count travel sachets</div>';
      AVAILABLE_FLAVORS.forEach(flavor => {
        const variant = DATA.variants.find(v => v.flavor === flavor && v.size === SIZE_8);
        if (!variant) return;
        const qty = state.bundle[bundleKey(flavor, SIZE_8)] || 0;
        html += renderBuilderRow(flavor, SIZE_8, getFlavorDotClass(flavor), qty, total < targetUnits, qty > 0, false);
      });
    }

    els.flavorBuilderRows.innerHTML = html;

    els.flavorBuilderRows.querySelectorAll('[data-mk-builder-key]').forEach(b => {
      b.addEventListener('click', () => {
        const key = b.dataset.mkBuilderKey;
        const action = b.dataset.mkBuilderAction;
        const target = onetimeTierBagCount(state.onetimeTier);
        if (action === '+' && bundleTotalQty() < target) {
          state.bundle[key] = (state.bundle[key] || 0) + 1;
        } else if (action === '-' && state.bundle[key] > 0) {
          state.bundle[key]--;
        }
        render();
      });
    });

    if (els.flavorBuilderCount) {
      els.flavorBuilderCount.textContent = total + ' of ' + targetUnits + ' unit' + (targetUnits === 1 ? '' : 's') + ' selected';
    }
    if (els.flavorBuilderSummary) {
      els.flavorBuilderSummary.classList.toggle('is-warn', total !== targetUnits);
    }
  };

  const calculatePrice = () => {
    if (state.mode === 'subscribe') {
      const { baseTotal, finalTotal } = calculateSubscribeTierPrice(state.subscribeTier);
      return { qty: state.subscribeTier, finalTotal, strikeTotal: baseTotal };
    }

    const tier = state.onetimeTier;

    if (tier === 1) {
      const { finalTotal } = calculateOnetimeTier1Price();
      return { qty: 1, finalTotal, strikeTotal: null };
    }

    const total = bundleTotalQty();
    if (total === 0) {
      const { baseTotal, finalTotal } = calculateOnetimeTierStaticPrice(tier);
      return { qty: 0, finalTotal, strikeTotal: tier >= 3 ? baseTotal : null };
    }

    const baseTotal = calculateBundleBasePrice();
    const disc = onetimeTierDiscount(tier);
    const finalTotal = Math.round(baseTotal * (1 - disc));
    const strikeTotal = tier >= 3 ? baseTotal : null;
    return { qty: total, finalTotal, strikeTotal };
  };

  const render = () => {
    root.classList.toggle('mode-subscribe', state.mode === 'subscribe');
    root.classList.toggle('mode-onetime', state.mode === 'onetime');
    root.classList.toggle('tier-2plus', state.mode === 'onetime' && state.onetimeTier >= 2);

    root.querySelectorAll('[data-mk-mode]').forEach(t => {
      t.classList.toggle('is-active', t.dataset.mkMode === state.mode);
    });

    if (els.cadenceRow) els.cadenceRow.classList.toggle('is-hidden', state.mode !== 'subscribe');
    if (els.cadenceLabel) els.cadenceLabel.style.display = state.mode === 'subscribe' ? '' : 'none';

    root.querySelectorAll('[data-mk-cadence]').forEach(b => {
      b.classList.toggle('is-active', b.dataset.mkPlanId === String(state.cadencePlanId));
    });

    if (state.mode === 'subscribe') updateSubscribeTierCards();

    if (state.mode === 'onetime') {
      updateOnetimeTierCards();
      const showBuilder = state.onetimeTier >= 2;
      if (els.onetimeTiersWrap) {
        els.onetimeTiersWrap.classList.toggle('show-builder', showBuilder);
      }
      if (showBuilder) {
        renderFlavorBuilderRows();
      } else {
        Object.keys(state.bundle).forEach(k => { state.bundle[k] = 0; });
      }
    }

    root.querySelectorAll('[data-mk-flavor]').forEach(b => {
      b.classList.toggle('is-active', b.dataset.mkFlavor === state.flavor);
    });
    if (els.flavorLabel) els.flavorLabel.textContent = state.flavor;

    root.querySelectorAll('[data-mk-size]').forEach(b => {
      b.classList.toggle('is-active', b.dataset.mkSize === state.size);
    });
    if (els.sizeLabel) els.sizeLabel.textContent = state.size;

    const { finalTotal, strikeTotal } = calculatePrice();
    if (els.priceMain) els.priceMain.textContent = fmt(finalTotal);
    if (els.priceStrike) {
      if (strikeTotal && strikeTotal !== finalTotal) {
        els.priceStrike.textContent = fmt(strikeTotal);
        els.priceStrike.style.display = '';
      } else {
        els.priceStrike.style.display = 'none';
      }
    }

    if (els.purchaseBody) {
      els.purchaseBody.textContent = state.mode === 'subscribe'
        ? 'Skip or cancel anytime. No commitment.'
        : 'One-time purchase. No commitment.';
    }

    if (els.ppsLine) {
      if (state.mode === 'onetime') {
        let totalServings;
        if (state.onetimeTier === 1) {
          totalServings = (state.size && state.size.indexOf('15') !== -1) ? 15 : 8;
        } else {
          totalServings = calculateBundleServings();
        }
        if (totalServings > 0 && finalTotal > 0) {
          const ppsCents = calculatePPS(finalTotal, totalServings);
          els.ppsLine.textContent = fmt(ppsCents) + ' per serving · ' + totalServings + ' servings total';
        } else {
          els.ppsLine.textContent = '';
        }
      } else {
        els.ppsLine.textContent = '';
      }
    }

    if (els.atcPrice) els.atcPrice.textContent = '— ' + fmt(finalTotal);

    if (state.mode === 'onetime' && state.onetimeTier >= 2) {
      const target = onetimeTierBagCount(state.onetimeTier);
      const total = bundleTotalQty();
      if (els.atc) els.atc.disabled = total !== target;
    } else if (els.atc) {
      els.atc.disabled = false;
    }

    syncFormFields();
  };

  const syncFormFields = () => {
    if (state.mode === 'subscribe') {
      const variant = find15ServingVariant(state.flavor);
      if (variant && els.variantInput) els.variantInput.value = variant.id;
      if (els.qtyInput) els.qtyInput.value = state.subscribeTier;
      if (els.sellingPlanInput) els.sellingPlanInput.value = state.cadencePlanId || '';
      if (els.bundleProp) els.bundleProp.value = '';
      return;
    }

    if (state.onetimeTier === 1) {
      const variant = findVariant(state.flavor, state.size);
      if (variant && els.variantInput) els.variantInput.value = variant.id;
      if (els.qtyInput) els.qtyInput.value = 1;
      if (els.bundleProp) els.bundleProp.value = '';
    } else {
      const composition = Object.entries(state.bundle)
        .filter(([_, qty]) => qty > 0)
        .map(([key, qty]) => {
          const [flavor, size] = key.split('__');
          return `${flavor} ${size} × ${qty}`;
        })
        .join(', ');
      if (els.bundleProp) els.bundleProp.value = composition;
    }
    if (els.sellingPlanInput) els.sellingPlanInput.value = '';
  };

  const submitBundle = async (e) => {
    e.preventDefault();
    const items = [];
    Object.entries(state.bundle).forEach(([key, qty]) => {
      if (qty <= 0) return;
      const [flavor, size] = key.split('__');
      const variant = findVariant(flavor, size);
      if (!variant) return;
      items.push({
        id: variant.id,
        quantity: qty,
        properties: {
          _bundle: 'mikuna-bundle',
          _bundle_total: bundleTotalQty()
        }
      });
    });

    if (items.length === 0) return;

    try {
      els.atc.disabled = true;
      const atcText = els.atc.querySelector('.mk-atc-text');
      if (atcText) atcText.textContent = 'Adding...';

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) throw new Error('Cart add failed');
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      window.location.href = '/cart';
    } catch (err) {
      console.error('[Mikuna PDP] Bundle add failed', err);
      els.atc.disabled = false;
      const atcText = els.atc.querySelector('.mk-atc-text');
      if (atcText) atcText.textContent = 'Add to cart';
      alert('Sorry, there was a problem adding your bundle. Please try again.');
    }
  };

  // EVENT BINDINGS
  root.querySelectorAll('[data-mk-mode]').forEach(t => {
    t.addEventListener('click', () => {
      const newMode = t.dataset.mkMode;
      if (newMode === state.mode) return;
      state.mode = newMode;
      Object.keys(state.bundle).forEach(k => { state.bundle[k] = 0; });
      if (newMode === 'subscribe') {
        if (SIZE_15) state.size = SIZE_15;
      } else {
        state.onetimeTier = 1;
      }
      updateGalleryImage(state.flavor);
      render();
    });
  });

  if (els.crossSell) {
    els.crossSell.addEventListener('click', () => {
      state.mode = 'subscribe';
      state.subscribeTier = state.onetimeTier === 1 ? 1 : Math.min(state.onetimeTier, SUBSCRIBE_MAX_TIER);
      if (SIZE_15) state.size = SIZE_15;
      Object.keys(state.bundle).forEach(k => { state.bundle[k] = 0; });
      updateGalleryImage(state.flavor);
      render();
    });
  }

  root.querySelectorAll('[data-mk-tier]').forEach(b => {
    b.addEventListener('click', () => {
      state.subscribeTier = parseInt(b.dataset.mkTier, 10);
      render();
    });
  });

  root.querySelectorAll('[data-mk-onetime-tier]').forEach(b => {
    b.addEventListener('click', (e) => {
      if (e.target.closest('[data-mk-tier4-action]')) return;
      state.onetimeTier = parseInt(b.dataset.mkOnetimeTier, 10);
      Object.keys(state.bundle).forEach(k => { state.bundle[k] = 0; });
      updateGalleryImage(state.flavor);
      render();
    });
  });

  root.querySelectorAll('[data-mk-tier4-action]').forEach(b => {
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      const action = b.dataset.mkTier4Action;
      if (action === '+' && state.tier4Bags < ONETIME_TIER4_MAX) state.tier4Bags++;
      else if (action === '-' && state.tier4Bags > ONETIME_TIER4_MIN) state.tier4Bags--;
      Object.keys(state.bundle).forEach(k => { state.bundle[k] = 0; });
      state.onetimeTier = 4;
      render();
    });
  });

  root.querySelectorAll('[data-mk-cadence]').forEach(b => {
    b.addEventListener('click', () => {
      state.cadencePlanId = b.dataset.mkPlanId;
      render();
    });
  });

  root.querySelectorAll('[data-mk-flavor]').forEach(b => {
    b.addEventListener('click', () => {
      if (b.disabled) return;
      state.flavor = b.dataset.mkFlavor;
      updateGalleryImage(state.flavor);
      render();
    });
  });

  root.querySelectorAll('[data-mk-size]').forEach(b => {
    b.addEventListener('click', () => {
      state.size = b.dataset.mkSize;
      updateGalleryImage(state.flavor);
      render();
    });
  });

  if (els.form) {
    els.form.addEventListener('submit', (e) => {
      if (state.mode === 'onetime' && state.onetimeTier >= 2) submitBundle(e);
    });
  }

  render();
  initGalleryThumbs();
})();
/**
 * MIKUNA — Auto-add Free Chimborazo Shaker (v1)
 *
 * Watches the cart for Chocho items. When ONE-TIME qty >= 3, auto-adds the
 * Chimborazo Shaker. The native BXGY discount rule zeroes its price at
 * checkout. Removes the auto-gift if qty drops below 3. Subscriptions
 * are excluded (they don't qualify for the BXGY rule).
 */
(function () {
  'use strict';

  // ⚠️ VERIFY these handles match your Shopify product URLs
  const CHOCHO_HANDLE = 'chocho-plant-protein-by-mikuna';
  const SHAKER_HANDLE = 'chimborazo-shaker-bottle';
  const THRESHOLD = 3;
  const GIFT_PROP = '_auto_gift';

  let shakerVariantId = null;
  let isReconciling = false;
  const originalFetch = window.fetch.bind(window);

  const getShakerVariantId = async () => {
    if (shakerVariantId) return shakerVariantId;
    try {
      const res = await originalFetch(`/products/${SHAKER_HANDLE}.js`);
      if (!res.ok) {
        console.warn('[Mikuna gift] Shaker product not found:', SHAKER_HANDLE);
        return null;
      }
      const product = await res.json();
      const variant = product.variants.find(v => v.available) || product.variants[0];
      shakerVariantId = variant ? variant.id : null;
      return shakerVariantId;
    } catch (e) {
      console.error('[Mikuna gift] Error fetching shaker:', e);
      return null;
    }
  };

  const getCart = async () => {
    try {
      const res = await originalFetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  };

  // Count ONLY one-time Chocho qty (subscriptions don't qualify)
  const countOneTimeChocho = (cart) => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => {
      if (item.handle !== CHOCHO_HANDLE) return sum;
      if (item.selling_plan_allocation) return sum;
      return sum + item.quantity;
    }, 0);
  };

  const findAutoShaker = (cart) => {
    if (!cart || !cart.items) return null;
    return cart.items.find(item =>
      item.handle === SHAKER_HANDLE &&
      item.properties &&
      item.properties[GIFT_PROP] === 'true'
    );
  };

  const findAnyShaker = (cart) => {
    if (!cart || !cart.items) return null;
    return cart.items.find(item => item.handle === SHAKER_HANDLE);
  };

  const addAutoShaker = async () => {
    const variantId = await getShakerVariantId();
    if (!variantId) return;
    try {
      await originalFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: {
              [GIFT_PROP]: 'true',
              'Gift': 'Free with 3+ bags'
            }
          }]
        })
      });
    } catch (e) {
      console.error('[Mikuna gift] Failed to add shaker:', e);
    }
  };

  const removeAutoShaker = async (lineKey) => {
    try {
      await originalFetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });
    } catch (e) {
      console.error('[Mikuna gift] Failed to remove shaker:', e);
    }
  };

  const refreshCartUI = () => {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    window.dispatchEvent(new CustomEvent('cart:updated'));
  };

  const reconcile = async () => {
    if (isReconciling) return;
    isReconciling = true;
    try {
      const cart = await getCart();
      if (!cart) return;

      const chochoQty = countOneTimeChocho(cart);
      const autoShaker = findAutoShaker(cart);
      const anyShaker = findAnyShaker(cart);

      if (chochoQty >= THRESHOLD) {
        if (!anyShaker) {
          await addAutoShaker();
          refreshCartUI();
        }
      } else {
        if (autoShaker) {
          await removeAutoShaker(autoShaker.key);
          refreshCartUI();
        }
      }
    } finally {
      isReconciling = false;
    }
  };

  // Intercept any cart mutation, then reconcile
  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
    const result = originalFetch.apply(this, args);

    if (/\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/.test(url)) {
      result.then(() => {
        if (!isReconciling) setTimeout(reconcile, 150);
      }).catch(() => {});
    }

    return result;
  };

  // Initial check on page load
  const init = () => setTimeout(reconcile, 300);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();